import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useEffect, useMemo, useState } from 'react';
import { useFinance, usePreferences } from '../../../contexts';
import { getScaleColorsByRange } from '../colorScale';
import styles from './TopReceitas.module.css';

const MAX_BARS = 8;

interface TooltipProps {
    active?: boolean;
    payload?: Array<{ value: number; name: string }>;
    label?: string;
    formatCurrency: (value: number) => string;
}

const CustomTooltip = ({ active, payload, label, formatCurrency }: TooltipProps) => {
    if (active && payload?.length) {
        return (
            <div className={styles.tooltip}>
                <p className={styles.tooltipLabel}>{label}</p>
                <p>
                    {formatCurrency(payload[0].value)}
                </p>
            </div>
        );
    }
    return null;
};

export function TopReceitas() {
    const { distribuicaoSubReceitas } = useFinance();
    const [isSmall, setIsSmall] = useState(false);
    const { formatCurrency, t } = usePreferences();

    useEffect(() => {
        const mql = window.matchMedia('(max-width: 480px)');
        setIsSmall(mql.matches);
        const handler = (e: MediaQueryListEvent) => setIsSmall(e.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    const { data, colors } = useMemo(() => {
        const sliced = distribuicaoSubReceitas.slice(0, MAX_BARS);
        const values = sliced.map((d) => d.valor);
        const colors = getScaleColorsByRange(values, 'income');

        const data = sliced.map((item, index) => ({
            name: item.categoria,
            valor: item.valor,
            color: colors[index],
        }));

        return { data, colors };
    }, [distribuicaoSubReceitas]);

    const formatXAxis = (value: number) => `${(value / 1000).toFixed(0)}k`;

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>{t('chart.topReceitas')}</h3>
            <span className={styles.subtitle}>{t('chart.maioresReceitas')}</span>

            <div className={styles.chartContainer}>
                {data.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={data}
                            layout="vertical"
                            margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                horizontal={false}
                                stroke="#e5e7eb"
                            />
                            <XAxis
                                type="number"
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={formatXAxis}
                                tick={{ fontSize: isSmall ? 10 : 12, fill: '#9ca3af' }}
                            />
                            <YAxis
                                type="category"
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: isSmall ? 10 : 12, fill: '#6b7280' }}
                                width={isSmall ? 60 : 100}
                            />
                            <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                            <Bar dataKey="valor" radius={[0, 4, 4, 0]} animationDuration={1500}>
                                {data.map((_, index) => (
                                    <Cell key={index} fill={colors[index]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className={styles.empty}>{t('common.semDados')}</div>
                )}
            </div>
        </div>
    );
}
